import React, { useCallback } from 'react'
import classNames from 'classnames'
import { When } from 'react-if'
import { LinkButton } from '@/atoms/Button'
import { AsLabel, ParagraphMD, TitleSM } from '@/atoms/Text'
import { IconColor } from '@/atoms/utils'
import { paths } from '@/constants/paths'
import { useSocialSharing } from '@/organisms/SocialSharing/SocialSharingSOF'
import { useUser } from '@/services/UserService'
import { useSafeTrack } from '@/utils/analytics'
import { useLoginUrl } from '@/utils/auth-utils'
import { useTranslate } from '@/utils/translate/translate-client'

interface SharingBlockProps {
  projectSlug: string
  className?: string
  vertical?: boolean
  hideImpact?: boolean
  showTitle?: boolean
  iconColor?: IconColor
  iconButtonClassName?: string
}

export const SharingBlock: React.FC<SharingBlockProps> = ({
  projectSlug,
  className,
  vertical = false,
  hideImpact,
  showTitle = true,
  iconColor = 'black',
  iconButtonClassName,
}) => {
  const track = useSafeTrack()
  const { t } = useTranslate('common')
  const { user } = useUser()
  const { loginUrl, trackLoginStarted } = useLoginUrl()
  const socials = useSocialSharing({ url: `${paths.getBaseURL()}${paths.payItForward.index}/${projectSlug}` })

  const trackShare = useCallback(
    (network: string) => {
      track('PIF Share Clicked', {
        type: network,
        projectSlug: projectSlug,
      })
    },
    [track, projectSlug],
  )

  return (
    <div className={`col-span-12 mx-auto w-full flex-col justify-center px-4 text-center md:col-span-8 ${className}`}>
      <div className={classNames('flex w-full justify-between')}>
        {showTitle && (
          <TitleSM
            className={classNames('align-middle my-auto hidden md:block font-bold !text-4xl', {
              'col-span-2': !vertical,
            })}
            weight="bold"
            as={AsLabel}
            color="black"
          >
            Share
          </TitleSM>
        )}
        {socials?.links?.map(({ name, href, onClick, icon: Icon }) => {
          if (href) {
            return (
              <a
                key={name}
                className={classNames(
                  'focus-visible:right-offset-0 flex h-10 w-10 flex-row items-center justify-center gap-2 rounded-full bg-core-gray-300 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent sm:h-12 sm:w-12',
                  iconButtonClassName,
                )}
                href={href}
                rel="noreferrer"
                target="_blank"
                onClick={() => {
                  trackShare?.(name)
                }}
              >
                <Icon color={iconColor} />
                <When condition={vertical}>
                  <ParagraphMD className="my-auto cursor-pointer" color="black">
                    {name}
                  </ParagraphMD>
                </When>
              </a>
            )
          } else if (onClick) {
            return (
              <button
                key={name}
                className={classNames(
                  'focus-visible:right-offset-0 flex h-10 w-10 flex-row items-center justify-center gap-2 rounded-full bg-core-gray-300 focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-transparent sm:h-12 sm:w-12',
                  iconButtonClassName,
                )}
                onClick={() => {
                  trackShare?.('Copy to Clipboard')
                  onClick()
                }}
              >
                <Icon color={iconColor} size={25} />
                <When condition={vertical}>
                  <ParagraphMD className="my-auto cursor-pointer" color="black">
                    {name}
                  </ParagraphMD>
                </When>
              </button>
            )
          }
        })}
      </div>
      {!user && !hideImpact && (
        <ParagraphMD className="mt-6 text-center text-core-gray-700">
          <LinkButton
            className="m-0 inline border-none p-0 text-core-gray-950"
            href={loginUrl}
            variant="plain"
            onClick={() => trackLoginStarted('bottom-sharing-block')}
          >
            {t('loginOrCreateAFreeAccount', 'Login or Create a free account')}
          </LinkButton>{' '}
          {t(
            'toSeeHowManyPeopleYouHaveImpactedBecauseOfYourShare',
            'to see how many people you impacted because of your share.',
          )}
        </ParagraphMD>
      )}
    </div>
  )
}
